import { validateSelect } from '../utils/form'
import { observeRestrictAbsoluteToViewport } from '../utils/restrictAbsoluteToViewport'

export const NFB_SELECT_HAS_CHANGED_EVENT = 'nfbselect:valuehaschanged'

interface SelectManagerOptions {
  validate?: boolean
}

interface Option {
  optionElement: HTMLElement
  optionElementIsVisible: boolean
}

export class Select {
  options: SelectManagerOptions
  selectNode: HTMLElement | null = null
  buttonNode: HTMLElement | null = null
  listNode!: HTMLElement
  inputNode!: HTMLElement
  inputContainerNode!: HTMLElement
  hiddenInputNode!: HTMLInputElement

  currentOption!: HTMLElement
  selectedTextValue: string | undefined
  allOptions: Option[] = []
  filteredOptions: HTMLElement[] = []
  firstOption!: HTMLElement
  lastOption!: HTMLElement

  filter: string = ''
  optionIntersectionObserver: IntersectionObserver | undefined | null
  listMutationObserver: MutationObserver | undefined

  listHasVisualFocus: boolean = false
  optionInteraction: boolean = false

  private _isExpanded: boolean = false

  constructor (selectNode: HTMLElement, options: SelectManagerOptions = {}) {
    this.options = options
    this.selectNode = selectNode
    this.buttonNode = this.selectNode.querySelector('[data-ui-el="nfb-form-select-button"]') as HTMLElement
    this.inputNode = this.selectNode.querySelector('[role="combobox"]') as HTMLElement
    this.inputContainerNode = this.selectNode.querySelector('[data-ui-el="nfb-form-select-input"]') as HTMLElement
    this.listNode = this.selectNode.querySelector('[role="listbox"]') as HTMLElement
    this.hiddenInputNode = this.selectNode.querySelector('[data-ui-el="nfb-form-selected-option"]') as HTMLInputElement

    this.initOptions()
    this.initListMutationObserver()

    this.inputContainerNode.addEventListener('click', () => {
      this.isExpanded ? this.closeList() : this.openList()
    })
    this.inputNode.addEventListener('blur', this.onSelectExit)
    this.selectNode.addEventListener('keydown', this.onInputKeydown)
    this.selectNode.addEventListener('keyup', this.onInputKeyup)
    this.buttonNode.addEventListener('focus', this.onButtonFocus)
    this.buttonNode.addEventListener('blur', this.onButtonBlur)
    window.addEventListener('mousedown', this.onBackgroundMousedown, true)

    const defaultValue = this.hiddenInputNode.value
    if (defaultValue) {
      const relatedOption = this.allOptions.find(o => o.optionElement.dataset.value === defaultValue)
      if (relatedOption) {
        this.selectOption(relatedOption.optionElement)
      } else {
        this.setOptionToInput()
      }
    } else {
      this.setOptionToInput()
    }

    observeRestrictAbsoluteToViewport(
      this.listNode,
      null,
      false,
      ['data-list-is-open']
    )
  }

  initOptions = (): void => {
    this.allOptions = []
    const options: HTMLElement[] = Array.from(this.selectNode?.querySelectorAll('[role="option"]') ?? [])

    if (this.optionIntersectionObserver) this.optionIntersectionObserver.disconnect()
    this.optionIntersectionObserver = new IntersectionObserver(this.optionIsIntersecting, { root: this.listNode, threshold: [0.999] })

    this.selectNode?.classList.toggle('nfb-form__select-container--disabled', !(options?.length))

    if (options?.length) {
      options.forEach((option, index) => {
        this.optionIntersectionObserver?.observe(option)
        this.allOptions.push({
          optionElement: option,
          optionElementIsVisible: false
        })
        option.dataset.index = index.toString()
        option.addEventListener('click', this.onOptionClick)
        option.addEventListener('mouseover', this.onOptionMouseover)
        option.addEventListener('mousedown', () => { this.optionInteraction = true })

        if (option.hasAttribute('selected')) {
          this.selectOption(option)
        }
      })

      this.firstOption = this.allOptions[0].optionElement
      this.lastOption = this.allOptions.at(-1)!.optionElement
    }
  }

  shouldRefreshOptions = (): void => {
    this.hiddenInputNode.value = this.selectedTextValue = ''
    this.setOptionToInput(null)
    this.initOptions()
  }

  initListMutationObserver = (): void => {
    if (!this.listMutationObserver) {
      this.listMutationObserver = new MutationObserver(this.shouldRefreshOptions)
    }
    this.listMutationObserver.observe(this.listNode, { childList: true })
  }

  /* Setters & Getters */

  get isExpanded (): boolean { return this._isExpanded }

  set isExpanded (value: boolean) {
    this._isExpanded = value
    this.buttonNode?.setAttribute('aria-expanded', value.toString())
    this.inputNode?.setAttribute('aria-expanded', value.toString())
  }

  destroy (): void {
    this.optionIntersectionObserver?.disconnect()
    this.optionIntersectionObserver = null
  }

  onSelectExit = (): void => {
    // if function called on blur() while an option has been clicked,
    // skip execution, the function will be called after the select option
    // process is completed
    if (!this.optionInteraction) {
      this.setOptionToInput(this.selectedTextValue ?? null)
      this.filter = ''
      this.filterOptions(false)

      if (this.options.validate && this.selectNode) {
        validateSelect(this.selectNode)
      }
    }
  }

  onInputKeyup = (event: KeyboardEvent): void => {
    const isPrintableCharacter: boolean = this.isPrintableCharacter(event.key)
    const key: string = event.key
    let flag: boolean = true

    if (isPrintableCharacter) {
      this.filter += event.key
    }

    switch (key) {
      case 'Escape':
      case 'Esc':
        flag = false
        break
      case 'Backspace':
        this.filter = this.getInputValue()
        this.filterOptions()
        break
      default:
        if (isPrintableCharacter) {
          this.inputContainerNode.dataset.inputIsActive = 'true'
          this.filterOptions()
        }
    }

    if (flag) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  onInputKeydown = (event: KeyboardEvent): void => {
    const key: string = event.key
    let flag: boolean = true

    switch (key) {
      case 'Enter':
        this.selectOption(this.currentOption)
        this.closeList()
        break
      case 'ArrowDown':
      case 'Down':
        if (this.listHasVisualFocus) {
          this.setFocusToNextOption()
        } else {
          this.openList()
        }
        break
      case 'ArrowUp':
      case 'Up':
        if (this.listHasVisualFocus) {
          this.setFocusToPreviousOption()
        } else {
          this.openList()
          this.setFocusToOption(this.lastOption)
        }
        break
      case 'Esc':
      case 'Escape':
        if (this.isExpanded) {
          this.closeList()
        }
        break
      default:
        flag = false
        break
    }

    if (flag) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  openList = (): void => {
    this.inputNode.focus()
    this.setFocusToOption(this.firstOption)

    if (this.listNode.dataset.listIsOpen === 'false') {
      this.listNode.dataset.listIsOpen = 'true'
      this.listNode.scrollTop = 0
      this.isExpanded = !this.isExpanded
      this.listHasVisualFocus = true
    }
  }

  closeList = (): void => {
    if (this.listNode.dataset.listIsOpen === 'true') {
      this.listNode.dataset.listIsOpen = 'false'
      this.inputNode?.setAttribute('aria-activedescendant', '')
      this.currentOption.classList.remove('nfb-form__option--focus')
      this.isExpanded = !this.isExpanded
    }
    this.listHasVisualFocus = false
  }

  onOptionClick = (event: any): void => {
    this.selectOption(event.target)
    this.closeList()
    this.onSelectExit()
  }

  selectOption = (option: HTMLElement): void => {
    this.allOptions.forEach(o => {
      if (o.optionElement.hasAttribute('data-ui-selected')) o.optionElement.removeAttribute('data-ui-selected')
    })
    option.setAttribute('data-ui-selected', true.toString())

    if (option.dataset.value) {
      this.hiddenInputNode.value = option.dataset.value
    }
    if (option.textContent) {
      this.selectedTextValue = option.textContent.trim()
      this.setOptionToInput(this.selectedTextValue)
    }
    this.optionInteraction = false
    this.selectNode?.dispatchEvent(new CustomEvent(NFB_SELECT_HAS_CHANGED_EVENT, { composed: false, bubbles: false }))
  }

  setOptionToInput = (optionText: string | null = null): void => {
    if (this.inputNode.tagName === 'INPUT') {
      (this.inputNode as HTMLInputElement).value = optionText ?? ''
    } else {
      this.inputNode.textContent = optionText ?? this.inputNode.getAttribute('placeholder') ?? ''
      this.inputNode.setAttribute('data-ui-is-placeholder', (optionText === null).toString())
    }
  }

  getInputValue = (): string => {
    return this.inputNode.tagName === 'INPUT'
      ? (this.inputNode as HTMLInputElement).value
      : this.inputNode.textContent ?? ''
  }

  setFocusToOption = (newOption: HTMLElement): void => {
    this.currentOption?.classList.remove('nfb-form__option--focus')
    this.currentOption = newOption
    this.currentOption?.classList.add('nfb-form__option--focus')
    this.inputNode?.setAttribute('aria-activedescendant', newOption.id)

    this.scrollToOption()
  }

  scrollToOption = (): void => {
    if (!this.allOptions[parseInt(this.currentOption.dataset.index!)].optionElementIsVisible) {
      this.currentOption.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  setFocusToPreviousOption = (): HTMLElement => {
    let newOption: HTMLElement

    if (this.currentOption === this.firstOption) {
      newOption = this.lastOption
    } else {
      const index = parseInt(this.currentOption.dataset.index!)
      newOption = this.allOptions[index - 1].optionElement
    }
    this.setFocusToOption(newOption)

    return newOption
  }

  setFocusToNextOption = (): HTMLElement => {
    let newOption: HTMLElement

    if (this.currentOption === this.lastOption) {
      newOption = this.firstOption
    } else {
      const index = parseInt(this.currentOption.dataset.index!)
      newOption = this.allOptions[index + 1].optionElement
    }
    this.setFocusToOption(newOption)

    return newOption
  }

  onOptionMouseover = (event: any): void => {
    this.setFocusToOption(event.currentTarget)
  }

  isPrintableCharacter = (str: string): boolean => {
    return str.length === 1 && !!(str.match(/\S| /) !== null)
  }

  filterOptions = (forceOpenOrClose: boolean = true): void => {
    // Disconnect temporarly listMutationObserver when filtering
    this.listMutationObserver?.disconnect()

    const filter = this.filter.toLowerCase()
    this.filteredOptions = []
    this.listNode.innerHTML = ''

    this.allOptions.forEach((option) => {
      if (filter.length === 0 || option.optionElement.textContent?.toLowerCase().indexOf(filter) === 0) {
        this.filteredOptions.push(option.optionElement)
        this.listNode.appendChild(option.optionElement)
      }
    })

    if (forceOpenOrClose) {
      if (this.filteredOptions.length > 0) {
        this.firstOption = this.filteredOptions[0]
        this.lastOption = this.filteredOptions.at(-1)!
        this.openList()
      } else {
        this.closeList()
      }
    }
    this.initListMutationObserver()
  }

  onBackgroundMousedown = (event: any): void => {
    if (this.selectNode !== null && !this.selectNode.contains(event.target)) {
      if (this.isExpanded) {
        this.closeList()
        this.inputContainerNode.dataset.inputIsActive = 'false'
        this.listHasVisualFocus = false
      }
    }
  }

  onButtonFocus = (): void => {
    this.selectNode?.setAttribute('is-focus', true.toString())
  }

  onButtonBlur = (): void => {
    this.selectNode?.setAttribute('is-focus', false.toString())
  }

  optionIsIntersecting = (entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
      const target = entry.target as HTMLElement
      const relatedOption = this.allOptions[parseInt(target.dataset.index!)]
      if (relatedOption != null) {
        relatedOption.optionElementIsVisible = entry.isIntersecting
      }
    }
  }
}
